const LINK_ONLY_PATTERN = /^(https?:\/\/|www\.)\S+$/i;
const WWW_PREFIX_PATTERN = /^www\./i;

/** A description that consists of nothing but a link. */
export interface ILinkOnly {
  /** Absolute http(s) url to open. */
  url: string;
  /** Custom link text (e.g. from <a href="...">katso täältä</a>), or null when the link text is the address itself. */
  label: string | null;
}

function toAbsoluteUrl(address: string): string | null {
  const trimmed: string = address.trim();
  if (!LINK_ONLY_PATTERN.test(trimmed)) return null;
  return WWW_PREFIX_PATTERN.test(trimmed) ? `https://${trimmed}` : trimmed;
}

/**
 * If the (rich text) HTML contains nothing but a single link, returns its url and custom text.
 * Handles a bare http(s)/www. address and a single <a href> whose text is the whole content.
 * Otherwise returns null. Tags are stripped and entities decoded
 * (DOMParser does not execute scripts or load resources).
 */
export function extractLinkOnly(html: string | null | undefined): ILinkOnly | null {
  if (!html || typeof html !== 'string') return null;

  const body: HTMLElement = new DOMParser().parseFromString(html, 'text/html').body;

  // textContent would glue separate paragraphs together, so inspect the text nodes one by one
  const textNodes: Text[] = [];
  const walker: TreeWalker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const node: Text = walker.currentNode as Text;
    if ((node.textContent ?? '').trim()) textNodes.push(node);
  }
  if (textNodes.length !== 1) return null;

  const text: string = (textNodes[0].textContent ?? '').trim();
  const anchor: HTMLAnchorElement | null = textNodes[0].parentElement?.closest('a[href]') ?? null;
  if (anchor) {
    const url: string | null = toAbsoluteUrl(anchor.getAttribute('href') ?? '');
    return url ? { url, label: LINK_ONLY_PATTERN.test(text) ? null : text } : null;
  }

  const url: string | null = toAbsoluteUrl(text);
  return url ? { url, label: null } : null;
}
